import { 
  SearchIcon, 
  BellIcon, 
  HelpCircleIcon,
  LayoutDashboardIcon,
  UsersIcon,
  BuildingIcon,
  CalculatorIcon,
  PackageIcon,
  SettingsIcon,
  CodeIcon
} from 'lucide-react';

// Import types
export type { 
  NavItem, 
  UserActionItem, 
  BreadcrumbItem, 
  NavigationSectionConfig,
  NavigationItemConfig,
  TopNavItem 
} from './types';

// Import section configurations
import { dashboardsSections } from './dashboards';
import { clientsPoliciesSections } from './clients-policies';
import { usersOrganizationsSections } from './users-organizations';
import { reportsFinanceSections } from './reports-finance';
import { productsTemplatesSections } from './products-templates';
import { systemAdminSections } from './system-admin';
import { developmentSections } from './development';

// Import utilities (internal functions with full signatures)
import { 
  getActiveTopNavId as getActiveTopNavIdInternal, 
  getSidebarSections as getSidebarSectionsInternal, 
  transformToNavItems as transformToNavItemsInternal, 
  generateBreadcrumbs as generateBreadcrumbsInternal 
} from './utils';

import type { UserActionItem, TopNavItem } from './types';

/**
 * Combined Navigation Configuration
 * All sections from separate files
 */
export const navigationConfig = [
  ...dashboardsSections,
  ...clientsPoliciesSections,
  ...usersOrganizationsSections,
  ...reportsFinanceSections,
  ...productsTemplatesSections,
  ...systemAdminSections,
  ...developmentSections
];

/**
 * Top Navigation Configuration
 * 
 * Each item now has an associated permission for visibility control
 */
export interface TopNavConfig {
  id: string;
  label: string;
  icon?: React.ReactNode;
  href?: string;
  permission: string; // Navigation permission required to see this top nav item
}

export const topNavigationItems: TopNavConfig[] = [
  {
    id: 'dashboards',
    label: 'Dashboards',
    icon: <LayoutDashboardIcon size={18} />,
    href: '/dashboard',
    permission: 'topnav:dashboards'
  },
  {
    id: 'clients-policies',
    label: 'Clients & Policies',
    icon: <UsersIcon size={18} />,
    href: '/admin/farmers',
    permission: 'topnav:clients-policies'
  },
  {
    id: 'users-organizations',
    label: 'Users & Organisations',
    icon: <BuildingIcon size={18} />,
    href: '/admin/organizations',
    permission: 'topnav:users-organizations'
  },
  {
    id: 'accounting-finance',
    label: 'Accounting & Finance',
    icon: <CalculatorIcon size={18} />,
    href: '/ledger',
    permission: 'topnav:accounting-finance'
  },
  {
    id: 'products-templates',
    label: 'Products & Templates',
    icon: <PackageIcon size={18} />,
    href: '/admin/products',
    permission: 'topnav:products-templates'
  },
  {
    id: 'system-admin',
    label: 'System Administration',
    icon: <SettingsIcon size={18} />,
    href: '/admin/roles',
    permission: 'topnav:system-admin'
  },
  {
    id: 'development',
    label: 'Development',
    icon: <CodeIcon size={18} />,
    href: '/dev/mock-payment-api',
    permission: 'topnav:development'
  }
];

// Backward compatibility - export as TopNavItem[] for AppLayout
export const topNavigationConfig: TopNavItem[] = topNavigationItems;

/**
 * User Actions Configuration
 */
export const userActionsConfig: UserActionItem[] = [
  {
    id: 'search',
    label: 'Search',
    icon: <SearchIcon size={16} />,
    onClick: () => {
      console.log('Global search clicked');
    }
  },
  {
    id: 'notifications',
    label: 'Notifications',
    icon: <BellIcon size={16} />,
    onClick: () => {
      console.log('Notifications clicked');
    }
  },
  {
    id: 'help',
    label: 'Help',
    icon: <HelpCircleIcon size={16} />,
    onClick: () => {
      console.log('Help clicked');
    }
  }
];

/**
 * Exported utility functions with simplified signatures
 * These wrap the internal functions and automatically inject navigationConfig
 */

export function getActiveTopNavId(pathname: string): string {
  return getActiveTopNavIdInternal(pathname, navigationConfig);
}

export function getSidebarSections(
  activeTopNavId: string,
  hasPermission: (permission: string) => boolean
) {
  return getSidebarSectionsInternal(activeTopNavId, navigationConfig, hasPermission);
}

export function transformToNavItems(
  activeTopNavId: string,
  currentPath: string,
  hasPermission: (permission: string) => boolean
) {
  return transformToNavItemsInternal(activeTopNavId, currentPath, navigationConfig, hasPermission);
}

export function generateBreadcrumbs(pathname: string) {
  return generateBreadcrumbsInternal(pathname, topNavigationConfig, navigationConfig);
}

/**
 * Get filtered top navigation items based on user permissions
 * Filters top nav items by topnav:* permissions
 */
export function getFilteredTopNavigation(
  hasPermission: (permission: string) => boolean
): TopNavItem[] {
  return topNavigationItems
    .filter(item => hasPermission(item.permission))
    .map(item => ({
      id: item.id,
      label: item.label,
      icon: item.icon,
      href: item.href
    }));
}

